import React, { useCallback, useRef, useState, useEffect } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  type NodeTypes,
  type ReactFlowInstance,
  type EdgeMouseHandler,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { makeStyles, tokens, Text, Button, Tooltip, Menu, MenuPopover, MenuList, MenuItem, MenuTrigger } from '@fluentui/react-components';
import { ZoomFit20Regular, ArrowSync20Regular, Dismiss20Regular, Copy20Regular, Eye20Regular, Link20Regular } from '@fluentui/react-icons';
import SchemaTableNode from './SchemaTableNode';
import type { SchemaTableNodeData } from './SchemaTableNode';
import { useSchemaLayout } from './useSchemaLayout';
import type { PhysicalTable, SchemaRelationship } from './types';

const useStyles = makeStyles({
  wrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  infoBanner: {
    padding: '10px 16px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    backgroundColor: tokens.colorNeutralBackground1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  bannerRow1: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  bannerTitle: {
    fontWeight: 600,
    fontSize: '14px',
  },
  bannerDescription: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    lineHeight: '16px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  bannerStats: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '11px',
    color: tokens.colorNeutralForeground2,
  },
  bannerMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    marginTop: '2px',
  },
  container: {
    flex: 1,
    position: 'relative',
    backgroundColor: tokens.colorNeutralBackground2,
    overflow: 'hidden',
  },
  toolbar: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    zIndex: 10,
    display: 'flex',
    gap: '4px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: '4px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
  },
  empty: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  edgeTooltip: {
    position: 'absolute',
    zIndex: 100,
    backgroundColor: tokens.colorNeutralBackgroundInverted,
    color: tokens.colorNeutralForegroundInverted,
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '11px',
    lineHeight: '16px',
    whiteSpace: 'pre-line',
    pointerEvents: 'none',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    maxWidth: '280px',
  },
  contextMenu: {
    position: 'fixed',
    zIndex: 200,
  },
});

const nodeTypes: NodeTypes = {
  schemaTable: SchemaTableNode,
};

interface SchemaCanvasProps {
  tables: PhysicalTable[];
  relationships: SchemaRelationship[];
  selectedTableId: string | null;
  highlightedTableIds: Set<string>;
  onTableSelect: (tableId: string | null) => void;
  onTableDoubleClick?: (tableId: string) => void;
  infoBanner?: {
    title: string;
    viewType: string;
    description?: string;
    businessQuestions?: string;
    whyImportant?: string;
  } | null;
}

const SchemaCanvas: React.FC<SchemaCanvasProps> = ({
  tables,
  relationships,
  selectedTableId,
  highlightedTableIds,
  onTableSelect,
  onTableDoubleClick,
  infoBanner: infoBannerData,
}) => {
  const styles = useStyles();
  const rfInstance = useRef<ReactFlowInstance | null>(null);
  const [edgeTooltip, setEdgeTooltip] = useState<{ text: string; x: number; y: number } | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; nodeId: string; label: string } | null>(null);

  const { nodes: layoutNodes, edges: layoutEdges } = useSchemaLayout({
    tables,
    relationships,
    selectedTableId,
    highlightedTableIds,
  });

  const [nodes, setNodes, onNodesChange] = useNodesState(layoutNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(layoutEdges);

  // Sync layout changes
  useEffect(() => {
    setNodes(layoutNodes);
    setEdges(layoutEdges);
  }, [layoutNodes, layoutEdges, setNodes, setEdges]);


  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: { id: string }) => {
      onTableSelect(node.id === selectedTableId ? null : node.id);
    },
    [onTableSelect, selectedTableId]
  );

  const onNodeDoubleClick = useCallback(
    (_: React.MouseEvent, node: { id: string }) => {
      onTableDoubleClick?.(node.id);
    },
    [onTableDoubleClick]
  );

  const onPaneClick = useCallback(() => {
    onTableSelect(null);
  }, [onTableSelect]);

  const handleFit = useCallback(() => {
    rfInstance.current?.fitView({ padding: 0.2 });
  }, []);

  const handleClearSelection = useCallback(() => {
    onTableSelect(null);
  }, [onTableSelect]);

  // Edge hover tooltip
  const onEdgeMouseEnter: EdgeMouseHandler = useCallback((event, edge) => {
    const tooltip = edge.data?.tooltip;
    if (tooltip) {
      setEdgeTooltip({ text: tooltip, x: event.clientX, y: event.clientY });
    }
  }, []);

  const onEdgeMouseLeave: EdgeMouseHandler = useCallback(() => {
    setEdgeTooltip(null);
  }, []);

  // Right-click context menu
  const onNodeContextMenu = useCallback((event: React.MouseEvent, node: { id: string; data: SchemaTableNodeData }) => {
    event.preventDefault();
    setContextMenu({ x: event.clientX, y: event.clientY, nodeId: node.id, label: node.data.label });
    onTableSelect(node.id);
  }, [onTableSelect]);

  // Keyboard: Escape to deselect
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onTableSelect(null);
        setContextMenu(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onTableSelect]);

  // Close context menu on click anywhere
  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [contextMenu]);

  const hasSelection = selectedTableId !== null || highlightedTableIds.size > 0;

  const viewTypeLabel = infoBannerData
    ? infoBannerData.viewType === 'multi_fact'
      ? 'Multi-fact view'
      : infoBannerData.viewType === 'dimension' ? 'Dimension view' : 'Fact view'
    : '';

  if (tables.length === 0) {
    return (
      <div className={styles.empty}>
        <Text style={{ color: tokens.colorNeutralForeground3 }}>
          No physical tables in this model yet.
        </Text>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {infoBannerData ? (
        <div className={styles.infoBanner}>
          <div className={styles.bannerRow1}>
            <span className={styles.bannerTitle}>{infoBannerData.title}</span>
            <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>{viewTypeLabel}</Text>
          </div>
          {infoBannerData.description && (
            <div className={styles.bannerDescription}>{infoBannerData.description}</div>
          )}
          {infoBannerData.whyImportant && (
            <div className={styles.bannerMeta}>
              <span><b>Why important:</b> {infoBannerData.whyImportant}</span>
            </div>
          )}
          {infoBannerData.businessQuestions && (
            <div className={styles.bannerMeta}>
              <span><b>Business questions:</b> {infoBannerData.businessQuestions}</span>
            </div>
          )}
        </div>
      ) : (
        <div className={styles.infoBanner}>
          <div className={styles.bannerStats}>
            <span>Physical schema · {tables.length} table{tables.length !== 1 ? 's' : ''} · {relationships.length} relationship{relationships.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
      )}
      <div className={styles.container}>
        <div className={styles.toolbar}>
          <Tooltip content="Fit to screen" relationship="label">
            <Button appearance="subtle" icon={<ZoomFit20Regular />} size="small" onClick={handleFit} />
          </Tooltip>
          <Tooltip content="Auto layout" relationship="label">
            <Button appearance="subtle" icon={<ArrowSync20Regular />} size="small" onClick={handleFit} />
          </Tooltip>
          {hasSelection && (
            <Tooltip content="Clear selection (Esc)" relationship="label">
              <Button appearance="subtle" icon={<Dismiss20Regular />} size="small" onClick={handleClearSelection} />
            </Tooltip>
          )}
        </div>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          onNodeDoubleClick={onNodeDoubleClick}
          onNodeContextMenu={onNodeContextMenu as any}
          onPaneClick={onPaneClick}
          onEdgeMouseEnter={onEdgeMouseEnter}
          onEdgeMouseLeave={onEdgeMouseLeave}
          onInit={(instance) => { rfInstance.current = instance; }}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          nodesDraggable={true}
          nodesConnectable={false}
          elementsSelectable={true}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={20} size={1} />
          <Controls showInteractive={false} />
          <MiniMap
            nodeStrokeWidth={2}
            pannable
            zoomable
            style={{ borderRadius: 6 }}
          />
        </ReactFlow>
        {edgeTooltip && (
          <div
            className={styles.edgeTooltip}
            style={{ left: edgeTooltip.x + 12, top: edgeTooltip.y - 40, position: 'fixed' }}
          >
            {edgeTooltip.text}
          </div>
        )}
        {contextMenu && (
          <div className={styles.contextMenu} style={{ left: contextMenu.x, top: contextMenu.y }}>
            <Menu open onOpenChange={() => setContextMenu(null)}>
              <MenuTrigger><span /></MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem
                    icon={<Eye20Regular />}
                    onClick={() => { onTableDoubleClick?.(contextMenu.nodeId); setContextMenu(null); }}
                  >
                    Inspect table
                  </MenuItem>
                  <MenuItem
                    icon={<Link20Regular />}
                    onClick={() => { onTableSelect(contextMenu.nodeId); setContextMenu(null); }}
                  >
                    Show relationships
                  </MenuItem>
                  <MenuItem
                    icon={<Copy20Regular />}
                    onClick={() => { navigator.clipboard.writeText(contextMenu.label); setContextMenu(null); }}
                  >
                    Copy table name
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          </div>
        )}
      </div>
    </div>
  );
};

export default SchemaCanvas;